import { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three/webgpu';
import { Fn, vec3, vec4, float, positionLocal, modelWorldMatrix, uniform, sin, uv } from 'three/tsl';
import { getTerrainHeight } from '../../core/shaders/terrainHelpers';
import { uTerrainAmp, uTerrainFreq, uTerrainSeed, uTime } from '../../core/shaders/uniforms';
import { useGameStore } from '../../core/store/gameStore';
import { useCheckpointStore } from '../../core/store/checkpointStore';
import {
  EXPERIENCES,
  CHECKPOINT_ENTER_RADIUS,
  CHECKPOINT_EXIT_RADIUS,
} from '../../content/experiences';

const BEAM_HEIGHT = 26;
const BEAM_RADIUS = 0.45;
const CORE_RADIUS = 0.1;
const GEM_HEIGHT = 3.2;

interface Beacon {
  beamMat: THREE.MeshBasicNodeMaterial;
  coreMat: THREE.MeshBasicNodeMaterial;
  gemMat: THREE.MeshBasicNodeMaterial;
  dim: ReturnType<typeof uniform>;
}

// Beacons sit on the procedural terrain by sampling the same TSL height
// function in their vertex stage (the ground itself is displaced in-shader).
function makeBeacon(colorHex: string, phase: number): Beacon {
  const dim = uniform(1.0);
  const color = new THREE.Color(colorHex);
  const terrainHeight = getTerrainHeight(uTerrainAmp, uTerrainFreq, uTerrainSeed);

  const positionNode = Fn(() => {
    const worldPos = modelWorldMatrix.mul(vec4(positionLocal, float(1.0))).xyz;
    const h = terrainHeight(worldPos.xz);
    return vec4(vec3(positionLocal.x, positionLocal.y.add(h), positionLocal.z), float(1.0));
  })();

  const pulse = float(0.75).add(sin(uTime.mul(2.0).add(float(phase))).mul(0.25));

  const beamMat = new THREE.MeshBasicNodeMaterial();
  beamMat.transparent = true;
  beamMat.depthWrite = false;
  beamMat.blending = THREE.AdditiveBlending;
  beamMat.side = THREE.DoubleSide;
  beamMat.colorNode = vec4(vec3(color.r, color.g, color.b).mul(1.6), float(1.0));
  beamMat.opacityNode = uv().y.oneMinus().mul(0.3).mul(pulse).mul(dim);
  beamMat.positionNode = positionNode;

  const coreMat = new THREE.MeshBasicNodeMaterial();
  coreMat.transparent = true;
  coreMat.depthWrite = false;
  coreMat.blending = THREE.AdditiveBlending;
  coreMat.side = THREE.DoubleSide;
  coreMat.colorNode = vec4(vec3(color.r, color.g, color.b).mul(3.0), float(1.0));
  coreMat.opacityNode = uv().y.oneMinus().mul(0.9).mul(dim);
  coreMat.positionNode = positionNode;

  const gemMat = new THREE.MeshBasicNodeMaterial();
  gemMat.colorNode = vec4(vec3(color.r, color.g, color.b).mul(2.5).mul(pulse), float(1.0));
  gemMat.positionNode = positionNode;

  return { beamMat, coreMat, gemMat, dim };
}

export function Checkpoints({ visible = true }: { visible?: boolean }) {
  const characterRef = useGameStore((state) => state.characterRef);
  const visitedIds = useCheckpointStore((state) => state.visitedIds);
  const gemRefs = useRef<(THREE.Mesh | null)[]>([]);
  const lastNearestRef = useRef<{ id: string | null; d: number }>({ id: null, d: Infinity });

  const beacons = useMemo(
    () => EXPERIENCES.map((e, i) => makeBeacon(e.color, i * 1.7)),
    []
  );

  // Dim beacons the visitor has already opened
  useEffect(() => {
    EXPERIENCES.forEach((e, i) => {
      beacons[i].dim.value = visitedIds.includes(e.id) ? 0.3 : 1.0;
    });
  }, [visitedIds, beacons]);

  useFrame((_, delta) => {
    gemRefs.current.forEach((gem) => {
      if (gem) gem.rotation.y += delta * 1.2;
    });

    const group = characterRef?.current;
    if (!group) return;

    const px = group.position.x;
    const pz = group.position.z;

    let nearestId: string | null = null;
    let nearestD = Infinity;
    for (const e of EXPERIENCES) {
      const d = Math.hypot(px - e.position[0], pz - e.position[1]);
      if (d < nearestD) {
        nearestD = d;
        nearestId = e.id;
      }
    }

    const { activeId, setActiveId, markVisited, setNearest } = useCheckpointStore.getState();

    // Throttle store writes: only when the nearest changes meaningfully
    const last = lastNearestRef.current;
    if (nearestId !== last.id || Math.abs(nearestD - last.d) > 1) {
      lastNearestRef.current = { id: nearestId, d: nearestD };
      setNearest(nearestId, nearestD);
    }

    if (activeId) {
      const active = EXPERIENCES.find((e) => e.id === activeId);
      if (active) {
        const d = Math.hypot(px - active.position[0], pz - active.position[1]);
        if (d > CHECKPOINT_EXIT_RADIUS) setActiveId(null);
      }
    } else if (nearestId && nearestD < CHECKPOINT_ENTER_RADIUS) {
      setActiveId(nearestId);
      markVisited(nearestId);
    }
  });

  return (
    <group visible={visible}>
      {EXPERIENCES.map((e, i) => (
        <group key={e.id} position={[e.position[0], 0, e.position[1]]}>
          <mesh material={beacons[i].beamMat} position={[0, BEAM_HEIGHT / 2, 0]}>
            <cylinderGeometry args={[BEAM_RADIUS, BEAM_RADIUS, BEAM_HEIGHT, 16, 1, true]} />
          </mesh>
          <mesh material={beacons[i].coreMat} position={[0, BEAM_HEIGHT / 2, 0]}>
            <cylinderGeometry args={[CORE_RADIUS, CORE_RADIUS, BEAM_HEIGHT, 8, 1, true]} />
          </mesh>
          <mesh
            ref={(m) => (gemRefs.current[i] = m)}
            material={beacons[i].gemMat}
            position={[0, GEM_HEIGHT, 0]}
          >
            <octahedronGeometry args={[0.5]} />
          </mesh>
        </group>
      ))}
    </group>
  );
}
